import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import DeliverySLA from './DeliverySLA';
import { mapSlaOptions } from './mapSlaResponse';
import { selectIsSlaFetching, selectSlaError, selectSlaOptions } from './selectSla';
import { PriceTypeTags, RawSlaOption, SlaCodeLabels, SlaOption } from './types';
import { showModal } from '../../../actions/shared/modal';
import { ModalTypes } from '../../../constants/index.js';
import {
  getB2cSellerConfig,
  getB2cSellerFlag,
  getMultiSellerSlaConfig
} from '../../../refresh/selectors/featureFlagsSelector';
import { selectSlaFromViewOption } from '../../../edd/actions';
import {
  segmentEventPush,
  getProductItemPDP,
  getDeliveryReturnDetailsViewedProps
} from '../../../modules/ga/GTMdatalayer';
import { pickSlaDeliveryNote } from '../../../refresh/molecules/DeliverySLA/constants';

interface StateProps {
  activePincode?: string;
  selectedSizeCode?: string;
  selectedSizeName?: string;
  isFetching?: boolean;
  error?: boolean;
  priceTypeTags?: PriceTypeTags;
  slaConfig?: Record<string, any>;
  /** raw v2 options for the selected sku, as stored by fetchDeliveryDetailsV2 */
  slaOptions?: RawSlaOption[];
  /** SLA-code → copy map (priorityDeliverySLPTagsConfig.PLP_BUCKET) */
  slaCodeLabels?: SlaCodeLabels;
  /** All SKU SLA entries for the active pincode (for multi-seller size flag). */
  slaDetailsBySku?: Record<string, { options?: RawSlaOption[] }>;
}

interface DispatchProps {
  openPincodeModal: () => void;
  selectSlaOption: (option: SlaOption) => void;
}

interface OwnProps {
  /** luxe theme, forwarded from the desktop PDP (newDesignLuxe) */
  luxe?: boolean;
  /** hide the pincode header row when the host renders its own */
  hideHeader?: boolean;
  /**
   * Heading rendered with the response only (not over the loading skeleton).
   * As a function it receives the delivery note this container derived from the
   * cards it is about to render — use that when the heading carries the note
   * (e.g. in a tooltip), so it can never disagree with the card count.
   */
  title?: React.ReactNode | ((deliveryNote: string) => React.ReactNode);
  /**
   * Note under the cards. Defaults to the config delivery note; pass "" where
   * the host already surfaces that note itself (wishlist shows it in the
   * heading tooltip, so the footnote would repeat it).
   */
  footerNote?: string;
  onSelectOption?: (option: SlaOption) => void;
  /**
   * PDP only — fire "Delivery And Return Details Viewed" once when the EDD
   * section is in view and SLA options are ready (same as msite PinCodeCard).
   * Leave off for cart / wishlist reuse.
   */
  trackDeliveryDetailsViewed?: boolean;
  productData?: any;
}

type Props = StateProps & DispatchProps & OwnProps;

/**
 * Desktop PDP container for the Delivery SLA UI.
 *
 * Data comes from the checkDeliveryDetails (v2) response that
 * `fetchDeliveryDetailsV2` stores under `edd.slaDetails[pincode][skuId]`. That
 * fetch is triggered on size selection (`selectSize` in actions/productData)
 * and on pincode submit (see the EDD view), mirroring the msite PDP.
 */
const DeliverySLAContainer: FC<Props> = ({
  activePincode,
  selectedSizeCode,
  selectedSizeName,
  isFetching,
  error,
  priceTypeTags,
  slaConfig,
  slaOptions,
  slaCodeLabels,
  slaDetailsBySku,
  openPincodeModal,
  selectSlaOption,
  luxe = false,
  hideHeader = false,
  title = null,
  footerNote,
  onSelectOption,
  trackDeliveryDetailsViewed = false,
  productData
}) => {
  const viewModel = useMemo(() => mapSlaOptions(slaOptions, priceTypeTags), [
    slaOptions,
    priceTypeTags
  ]);
  const deliveryNote = pickSlaDeliveryNote(
    slaConfig,
    viewModel?.options?.length || 0
  );
  const deliveryDetailsViewedFiredRef = useRef(false);
  const [deliverySectionInView, setDeliverySectionInView] = useState(false);

  useEffect(() => {
    if (!trackDeliveryDetailsViewed) return;
    deliveryDetailsViewedFiredRef.current = false;
  }, [trackDeliveryDetailsViewed, activePincode, selectedSizeCode]);

  useEffect(() => {
    if (!trackDeliveryDetailsViewed) return undefined;
    const el = document.getElementById('edd');
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setDeliverySectionInView(entry.isIntersecting);
      },
      { root: null, rootMargin: '0px', threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [trackDeliveryDetailsViewed]);

  useEffect(() => {
    if (!trackDeliveryDetailsViewed) return;
    if (!deliverySectionInView) return;
    if (deliveryDetailsViewedFiredRef.current) return;
    if (isFetching || !Array.isArray(slaOptions) || !slaOptions.length) return;
    deliveryDetailsViewedFiredRef.current = true;
    const slaViewedProps = getDeliveryReturnDetailsViewedProps({
      slaOptions,
      pincode: activePincode,
      sizeSelected: selectedSizeName,
      deliveryInfoText: deliveryNote,
      previousScreenName: 'pdp screen',
      slaDetailsBySku
    });
    segmentEventPush({
      event: 'Delivery And Return Details Viewed',
      cod_text: '',
      delivery_pincode: activePincode,
      priority_delivery_date: '',
      priority_delivery_fees: '',
      priority_delivery_flag: true,
      priority_delivery_order_by_date: '',
      products: [
        {
          ...getProductItemPDP(productData),
          product_return_window: productData?.fnlProductData?.returnWindow ?? 0,
          product_exchange_window: productData?.fnlProductData?.returnWindow ?? 0,
          ...slaViewedProps.product
        }
      ],
      rush_delivery_date: '',
      rush_delivery_fees: '',
      rush_delivery_flag: '',
      rush_order_by_date: '',
      screen_name: 'PDP screen',
      screen_source: 'pdp screen',
      size_selected_flag: selectedSizeCode ? true : false,
      standard_delivery_date: '',
      ...slaViewedProps.top
    });
  }, [
    trackDeliveryDetailsViewed,
    deliverySectionInView,
    slaOptions,
    isFetching,
    activePincode,
    selectedSizeName,
    selectedSizeCode,
    deliveryNote,
    productData,
    slaDetailsBySku
  ]);

  // SLA is fetched on size selection — render nothing until a size is chosen.
  if (!selectedSizeCode) return null;

  const handleSelectOption = (option: SlaOption) => {
    selectSlaOption(option);
    onSelectOption?.(option);
  };

  return (
    <DeliverySLA
      viewModel={viewModel}
      loading={isFetching}
      error={error}
      activePincode={activePincode}
      onChangePincode={openPincodeModal}
      onSelectOption={handleSelectOption}
      footerNote={footerNote === undefined ? deliveryNote : footerNote}
      luxe={luxe}
      hideHeader={hideHeader}
      title={typeof title === 'function' ? title(deliveryNote) : title}
      slaCodeLabels={slaCodeLabels}
    />
  );
};

const mapStateToProps = (state: any): StateProps => {
  const activePincode = state?.edd?.activePincode;
  const selectedSizeCode = state?.product?.selectedSizeCode || '';
  const slaConfig =
    (getB2cSellerFlag(state) ? getB2cSellerConfig(state) : getMultiSellerSlaConfig(state)) || {};

  return {
    activePincode,
    selectedSizeCode,
    selectedSizeName: state?.product?.selectedSizeName,
    isFetching: selectIsSlaFetching(state, activePincode, selectedSizeCode),
    error: selectSlaError(state, activePincode, selectedSizeCode),
    priceTypeTags: slaConfig?.priceTypeTags,
    slaConfig,
    slaOptions: selectSlaOptions(state, activePincode, selectedSizeCode),
    slaDetailsBySku: activePincode ? state?.edd?.slaDetails?.[activePincode] : null,
    slaCodeLabels: slaConfig?.slaTimeMessage
  };
};

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  openPincodeModal: () => dispatch(showModal(ModalTypes.EDD_PINCODE)),
  selectSlaOption: (option: SlaOption) => dispatch(selectSlaFromViewOption(option))
});

export default connect(mapStateToProps, mapDispatchToProps)(DeliverySLAContainer);
